/**
 * AICostPreview — inline "~$X for N items" chip next to AI-intensive
 * action buttons. Part K of the AI Usage Meter spec.
 *
 * - Reads the current $/unit rate from `/api/ai/cost-rate` (via the
 *   `useAICostPreview` hook, either passed in as `hook` or fetched
 *   locally).
 * - `scale` is the estimated units-per-item for this specific flow —
 *   the caller decides this because different flows consume
 *   different amounts of AI (FS mapping ≈ 0.5 units per account,
 *   note mapping same order of magnitude).
 * - When no rate is configured, renders "Cost estimate not available"
 *   rather than a fabricated number. The action itself still runs
 *   normally — this is informational only, never a spending cap or
 *   approval gate.
 */
import React from "react";
import { DollarSign } from "lucide-react";
import { useAICostPreview } from "@/hooks/useAICostPreview";

export function AICostPreview({ scale, count, unit = "items",
                                  hook, testId }) {
  // Two ways to pass the rate: import the hook once at the parent
  // (shared across siblings, avoids duplicate fetches) OR let the
  // component do it. Both are safe.
  const local = useAICostPreview();
  const src = hook || local;
  const units = Math.max(0, Number(scale || 0) * Number(count || 0));
  const label = `${count ?? 0} ${unit}`;
  const text = src.estimate(units, label);
  if (!src.loaded) return null;
  return (
    <span className="inline-flex items-center gap-1 text-[11px] italic text-muted-foreground"
          data-testid={testId}>
      <DollarSign className="h-3 w-3" />
      {text}
    </span>
  );
}

export default AICostPreview;
